/**
 * Overzichtspagina van het eigenaarsdashboard (hoofdstuk 6).
 *
 * Dit scherm verving de kaarten met voorbeeldwaarden die er sinds stage 0 stonden.
 * Die waren eerlijk over wat ze waren — er was nog geen boekingsdata — maar nu die
 * er wel is, is een voorbeeldgetal het gevaarlijkste wat hier kan staan: een
 * dashboard met een verzonnen cijfer is niet te onderscheiden van een dashboard
 * dat klopt.
 *
 * Vier keuzes bepalen wat er staat:
 *
 * 1. **Wat er te doen is, staat boven wat er is gebeurd.** Openstaande aanvragen
 *    en kanaalconflicten eerst; bezetting en omzet daaronder. Iemand die het
 *    dashboard opent, opent het meestal omdat er iets moet gebeuren.
 * 2. **Bezetting staat in nachten én in procenten.** Een percentage zonder noemer
 *    is niet te controleren, en 80% van vijf nachten is iets anders dan 80% van
 *    dertig.
 * 3. **Omzet is gesplitst.** Vastgelegd, aangevraagd en ontvangen zijn drie
 *    verschillende dingen (§17.13). Ze bij elkaar optellen zou de maand er beter
 *    uit laten zien dan hij is.
 * 4. **De periode is de lopende maand, met een stap terug en vooruit.** Geen
 *    datumkiezer die eerst ingevuld moet worden voordat er iets te zien is.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router'
import { useFormatters } from '@siteshowroom/i18n/react'
import type { Formatters } from '@siteshowroom/i18n'

import { useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import { fetchOverview, monthStart, overviewErrorMessageKey } from '../lib/overview-client'
import type { Overview, OverviewFailure, OverviewStay } from '../lib/overview-client'

/**
 * Een verblijfsdatum is een kalenderdatum in de tijdzone van de woning, geen
 * tijdstip; hij wordt in UTC gelezen én getoond. Zelfde reden als in
 * `bookings-page.tsx`: anders verschuift een aankomst in een andere tijdzone een
 * dag.
 */
function formatCalendarDate(formatters: Formatters, value: string): string {
  return formatters.formatDate(`${value}T00:00:00Z`, { timeZone: 'UTC' })
}

/** Promille naar een percentage met één decimaal: 226 wordt 22,6. */
function permilleToPercent(permille: number): number {
  return permille / 10
}

type State =
  | { readonly phase: 'loading' }
  | { readonly phase: 'empty' }
  | { readonly phase: 'failed'; readonly error: OverviewFailure }
  | { readonly phase: 'ready'; readonly overview: Overview }

function FailureNotice({ error }: { readonly error: OverviewFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(overviewErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

/** Eén regel in de lijst met aankomsten of aanvragen. */
function StayRow({
  stay,
  currency,
}: {
  readonly stay: OverviewStay
  readonly currency: string
}): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const name =
    stay.guestName !== ''
      ? stay.guestName
      : t(`dashboard.overview.kind.${stay.kind === 'booking' ? 'booking' : stay.kind}`)

  return (
    <li className="stay-row">
      <span className="stay-row__dates">
        {t('dashboard.overview.dates', {
          checkIn: formatCalendarDate(formatters, stay.checkIn),
          checkOut: formatCalendarDate(formatters, stay.checkOut),
        })}
      </span>
      {/* De naam van een gast is data en wordt niet vertaald; de omschrijving van
          een blokkade komt wél uit de catalogus. */}
      <span className="stay-row__guest">{name}</span>
      <span className="stay-row__nights">
        {t('dashboard.overview.nights', { count: stay.nights })}
      </span>
      {stay.totalCents > 0 && (
        <span className="stay-row__amount">
          {formatters.formatMoney(stay.totalCents, currency)}
        </span>
      )}
    </li>
  )
}

/** Een getal met zijn eenheid; de kern van dit scherm. */
function StatCard({
  title,
  value,
  note,
}: {
  readonly title: string
  readonly value: string
  readonly note?: string
}): ReactElement {
  return (
    <article className="card">
      <h2 className="card__title">{title}</h2>
      <p className="card__value">{value}</p>
      {note !== undefined && <p className="card__note">{note}</p>}
    </article>
  )
}

export function DashboardPage(): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [monthOffset, setMonthOffset] = useState(0)
  const [state, setState] = useState<State>({ phase: 'loading' })

  const controller = useRef<AbortController | null>(null)

  const loadResidences = useCallback(async (): Promise<void> => {
    const result = await listResidences()
    if (!result.ok) {
      setState({ phase: 'failed', error: result.error })
      return
    }
    setResidences(result.data)
    setResidenceId((current) => current ?? result.data[0]?.id ?? null)
    if (result.data.length === 0) {
      // Zonder woning valt er niets op te halen; blijven hangen op 'loading' zou
      // een scherm opleveren dat eeuwig laadt terwijl er niets komt.
      setState({ phase: 'empty' })
    }
  }, [])

  const loadOverview = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    controller.current?.abort()
    const next = new AbortController()
    controller.current = next

    const today = new Date()
    const result = await fetchOverview(
      residenceId,
      { from: monthStart(today, monthOffset), to: monthStart(today, monthOffset + 1) },
      { signal: next.signal },
    )
    if (next.signal.aborted) {
      return
    }
    if (!result.ok) {
      // Afgebroken is geen fout: dat gebeurt bij elke wissel van maand of woning.
      if (result.error.kind !== 'aborted') {
        setState({ phase: 'failed', error: result.error })
      }
      return
    }
    setState({ phase: 'ready', overview: result.data })
  }, [residenceId, monthOffset])

  useEffect(() => {
    // Zelfde patroon als de andere schermen: state wordt pas ná de await gezet.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidences()
  }, [loadResidences])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadOverview()
    return () => {
      controller.current?.abort()
    }
  }, [loadOverview])

  const overview = state.phase === 'ready' ? state.overview : null

  return (
    <section className="page" aria-labelledby="dashboard-heading">
      <h1 className="page__title" id="dashboard-heading">
        {t('dashboard.title')}
      </h1>

      {state.phase === 'empty' && (
        <p className="page__status" role="status">
          {t('dashboard.overview.noResidence')}
        </p>
      )}

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.overview.residence')}</span>
          <select
            className="field__input"
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((residence) => (
              // De naam komt uit de database en wordt niet vertaald.
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {state.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('dashboard.overview.loading')}
        </p>
      )}

      {state.phase === 'failed' && <FailureNotice error={state.error} />}

      {overview !== null && (
        <>
          <div className="period">
            <button
              className="button button--quiet"
              onClick={() => {
                setMonthOffset((current) => current - 1)
              }}
              type="button"
            >
              {t('dashboard.overview.period.previous')}
            </button>
            <span className="period__label">
              {formatters.formatDate(`${overview.from}T00:00:00Z`, {
                timeZone: 'UTC',
                year: 'numeric',
                month: 'long',
              })}
            </span>
            <button
              className="button button--quiet"
              onClick={() => {
                setMonthOffset((current) => current + 1)
              }}
              type="button"
            >
              {t('dashboard.overview.period.next')}
            </button>
            {monthOffset !== 0 && (
              <button
                className="button button--quiet"
                onClick={() => {
                  setMonthOffset(0)
                }}
                type="button"
              >
                {t('dashboard.overview.period.today')}
              </button>
            )}
          </div>

          {/* Wat er te doen is, staat boven wat er is gebeurd. */}
          {(overview.bookingsPending > 0 || overview.openConflicts > 0) && (
            <div className="notice notice--action" role="status">
              <p className="notice__title">{t('dashboard.overview.todo.title')}</p>
              <ul className="notice__list">
                {overview.pending.length > 0 && (
                  <li>
                    <Link to="/bookings">
                      {t('dashboard.overview.todo.requests', { count: overview.pending.length })}
                    </Link>
                  </li>
                )}
                {overview.openConflicts > 0 && (
                  <li>
                    <Link to="/channels">
                      {t('dashboard.overview.todo.conflicts', { count: overview.openConflicts })}
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          )}

          <div className="card-grid">
            <StatCard
              note={t('dashboard.overview.occupancy.note', {
                booked: overview.nightsBooked,
                total: overview.nightsInPeriod,
              })}
              title={t('dashboard.occupancy.title')}
              // Het procentteken komt uit de catalogus en niet uit `style:
              // 'percent'`: die opmaak vermenigvuldigt zelf met honderd, en de
              // waarde hier is al een percentage. Bovendien staat het teken in
              // sommige talen aan de andere kant of met een spatie ertussen.
              value={t('dashboard.overview.occupancy.value', {
                percent: permilleToPercent(overview.occupancyPermille),
              })}
            />
            {/* Direct naast de bezetting, want dat is het paar dat samen iets
                zegt: vol zijn is niet hetzelfde als goed draaien (hoofdstuk 7). */}
            <StatCard
              note={t('dashboard.overview.revpan.note')}
              title={t('dashboard.overview.revpan.title')}
              value={formatters.formatMoney(overview.revPanCents, overview.currency)}
            />
            <StatCard
              note={t('dashboard.overview.revenue.pending', {
                amount: formatters.formatMoney(overview.revenuePendingCents, overview.currency),
              })}
              title={t('dashboard.revenue.title')}
              value={formatters.formatMoney(overview.revenueConfirmedCents, overview.currency)}
            />
            <StatCard
              note={t('dashboard.overview.received.note')}
              title={t('dashboard.overview.received.title')}
              value={formatters.formatMoney(overview.receivedCents, overview.currency)}
            />
            <StatCard
              note={t('dashboard.overview.blocked.note', { nights: overview.nightsBlocked })}
              title={t('dashboard.bookings.title')}
              value={t('dashboard.overview.bookings.count', {
                count: overview.bookingsConfirmed,
              })}
            />
          </div>

          <section className="page__section">
            <h2 className="page__sectionTitle">{t('dashboard.upcoming.title')}</h2>
            {overview.upcoming.length === 0 ? (
              <p className="page__status">{t('dashboard.overview.upcoming.empty')}</p>
            ) : (
              <ul className="stay-list">
                {overview.upcoming.map((stay) => (
                  <StayRow currency={overview.currency} key={stay.id} stay={stay} />
                ))}
              </ul>
            )}
          </section>

          {overview.pending.length > 0 && (
            <section className="page__section">
              <h2 className="page__sectionTitle">{t('dashboard.bookings.pending.title')}</h2>
              <ul className="stay-list">
                {overview.pending.map((stay) => (
                  <StayRow currency={overview.currency} key={stay.id} stay={stay} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </section>
  )
}
